import { componentUtils, Toast } from '@mtfe/sweet-components';
import { eventBus } from '@mtfe/sweet-utils';
import { getErrorMessage } from '@mtfe/sweet-report';

import store, { initPoiData } from '../store';
import http from '../service';
import autoLogin from './autoLogin';
import { filterPoiList, filterCouponList, filterRedBag, filterNewCouponIDs, filterNewCoupon } from './filter';
import { showLoading, hideLoading } from './dom';
import { COUPON_REMAIN_STATUS, COUPON_STATUS, COUPON_TYPE } from '../constants';
import { experimentTrafficMiniProgramModal } from '../experiment';
import { handleError } from '.';
import { addCouponErrorToReport } from './report';

const { storeCommon } = store;
const { getGiftEventName } = componentUtils;

const changeCouponStatus = (poiList, giftID, success) => {
    poiList.forEach((poiInfo) => {
        if (poiInfo.giftInfo && poiInfo.giftInfo.gift_id === giftID && success) {
            poiInfo.giftInfo.status = COUPON_STATUS.STATUS_RECEIVED;
        }
    });
};
/**
 * fetchAllData - 请求商家数据列表
 *
 * @param {Object} targetStore - Store
 * @param {string} [fetchRecallBizId] - 替换store中的recallBizId，请求商家、红包、券数据的recallBizId,
 * @returns {Promise.<Object>} res
 */
export const fetchAllData = async (poiData, fetchRecallBizId = '') => {
    const { recallBizId, pageNo, giftId, pageDivideId } = poiData;
    const { searchKeyword } = store[recallBizId];

    if (storeCommon.phone === '' && pageNo > 1) {
        const autoLoginOptions = {
            cb: () => {},
            phone: storeCommon.phone,
            loginSuccessCb: () => {
                eventBus.$emit('loginSuccess', true);
            },
        };

        autoLogin(autoLoginOptions);
        return;
    }
    try {
        const res = await http.actGetPoiList({
            recallBizId: fetchRecallBizId || recallBizId,
            pageNo,
            searchKeyword,
            giftId,
            pageDivideId,
        });
        poiData.pageNo += 1;
        return res;
    } catch (error) {
        console.error(error);
        return null;
    }
};

export const fetchDataList = async (poiData, option = {}) => {
    const { isShowLoading = true } = option;
    try {
        if (storeCommon.isLoading) {
            return;
        }
        showLoading(isShowLoading);
        const res = await fetchAllData(poiData);
        return filterPoiList(res.infos);
    } catch (error) {
        console.error(error);
        return [];
    } finally {
        hideLoading();
    }
};

/**
 *
 * @param {object} targetStore 请求那个store下的数据
 * @param {string} fetchRecallBizId 使用不是store中的recallBizId请求
 * @param {string} giftInfoPoiRecallBizId 如果请求的是券，券另外需要商家需要使用giftInfoPoiRecallBizId请求券下的商家信息
 */
export const fetchAndDispatchDataToStore = async (targetStore, fetchRecallBizId = '', giftInfoPoiRecallBizId = '') => {
    try {
        const data = await fetchAllData(targetStore.poiData, fetchRecallBizId);
        const { recallBizId } = targetStore;

        const { gift_value: giftValue, infos, leftRecvNum } = data;

        if (infos && Array.isArray(infos) && infos.length) {
            // 过滤出商家
            const poiList = filterPoiList(infos);
            // 商家券
            const couponList = filterCouponList(infos);
            // 红包
            const redBagList = filterRedBag(infos);
            // 写入商家数据
            targetStore.poiData.poiList.push(...poiList);

            // 如果是红包列表，在各个红包下面初始化一个商家列表
            redBagList.forEach((poiInfo) => {
                const { gift_id: giftId } = poiInfo.giftInfo;
                poiInfo.giftInfo.poiData = initPoiData(giftInfoPoiRecallBizId || recallBizId, giftId);
            });
            // 券和红包哪个有写哪个，优先写入商家券
            targetStore.discountData.couponList.push(...couponList);
            targetStore.discountData.redBagList.push(...redBagList);
            if (giftValue) {
                targetStore.discountData.giftValue = Number(giftValue);
            }
        }
        if (leftRecvNum !== undefined && recallBizId === 'bigCouponHasInventory') {
            storeCommon.leftRecvNum = leftRecvNum;
        }
    } catch (error) {
        console.error(error);
        throw error;
    }
};

/**
 * 多张券领取
 * @param {Array} giftIDs
 * @param {string} recallBizId
 * @returns {Promise}
 */
export const fetchAllCoupon = async (couponList, recallBizId) => {
    const newCoupon = filterNewCoupon(couponList);
    const giftIDs = filterNewCouponIDs(couponList);
    try {
        if (giftIDs.length === 0) {
            Toast({ message: '领取成功', position: 'bottom' });
            // 已经全部领取
            return;
        }
        const recvRes = await http.actGeRecvVer4Multi(giftIDs, recallBizId);
        if (recvRes && recvRes.rets && recvRes.rets.length) {
            // 处理全部或者部分券领取成功的情况
            // 存储券的领取成功与否状态
            const recvResList = [];
            recvRes.rets.forEach((item, index) => {
                // mock 时，返回成功领券的数量与券数量不相同
                if (newCoupon[index]) {
                    const curGift = {
                        giftId: giftIDs[index],
                        status: item,
                        giftInfo: newCoupon[index].giftInfo,
                    };
                    recvResList.push(curGift);
                }
            });
            // 更新券状态
            recvResList.forEach((curGift) => {
                if (curGift.status === 0) {
                    changeCouponStatus(couponList, curGift.giftId, true); // curGift.status === 0 领券成功
                    const eventName = getGiftEventName(curGift.giftId);
                    eventBus.$emit(eventName, COUPON_STATUS.STATUS_RECEIVED, COUPON_REMAIN_STATUS.REMAIN_STATUS_RECEIVED);
                }
            });

            if (recvResList.length === recvResList.filter((item) => item.status === 0).length) {
                // 领券数 === 成功数
                // 修改一键领取按钮状态
                const redBagIndex = recvResList.findIndex((item) => {
                    const giftInfo = item.giftInfo;
                    return giftInfo && giftInfo.type === COUPON_TYPE.RED_BAG;
                });
                if (redBagIndex !== -1) {
                    experimentTrafficMiniProgramModal();
                } else {
                    Toast({ message: '领取成功', position: 'bottom' });
                }
            }
        }
    } catch (error) {
        addCouponErrorToReport(`一键领取异常，${getErrorMessage(error)}`, '一键领取异常');
        handleError(error, { showMessage: true, showConsole: true });
    }
};

/**
 * 单个红包领取
 * @param {*} poiInfo
 * @param {*} recallBizId
 */
export const fetchOneRedBag = async (poiInfo, recallBizId) => {
    const { giftInfo } = poiInfo;
    const { gift_id: giftId } = giftInfo;

    try {
        if (giftInfo.status === COUPON_STATUS.STATUS_NEW) {
            const giftIds = [giftId];
            const recvRes = await http.actGeRecvVer4Multi(giftIds, recallBizId);
            if (recvRes && recvRes.rets && recvRes.rets[0] === 0) {
                // rets === 0 标识领取成功
                giftInfo.status = COUPON_STATUS.STATUS_RECEIVED;
                // 触发领券成功的回调
                const eventName = getGiftEventName(giftId);
                eventBus.$emit(eventName, COUPON_STATUS.STATUS_RECEIVED);
                // Toast({ message: '领取成功', position: 'bottom' });
                experimentTrafficMiniProgramModal();
            } else {
                Toast({ message: '领取失败', position: 'bottom' });
            }
        }
    } catch (error) {
        addCouponErrorToReport(`红包领取异常，${getErrorMessage(error)}`, '红包领取异常');
        Toast({ message: '领取失败', position: 'bottom' });
        throw error;
    }
};

let callFetchOneCouponCount = 1;
/**
 *
 * @param {*} poiInfo
 * @param {*} recallBizId
 * @param {*} isCheckLeftRecvNum
 */
export const fetchOneCoupon = async (poiInfo, recallBizId, isCheckLeftRecvNum = true) => {
    try {
        const { giftInfo } = poiInfo;
        const { gift_id: giftId } = giftInfo;

        const getCoupon = async () => {
            const giftIds = [giftId];
            const recvRes = await http.actGeRecvVer4Multi(giftIds, recallBizId);
            if (recvRes && recvRes.rets && recvRes.rets[0] === 0) {
                // rets === 0 标识领取成功
                // 领取成功，改写券状态
                giftInfo.status = COUPON_STATUS.STATUS_RECEIVED;
                const eventName = getGiftEventName(giftId);
                // 券更新
                eventBus.$emit(eventName, COUPON_STATUS.STATUS_RECEIVED, COUPON_REMAIN_STATUS.REMAIN_STATUS_RECEIVED);
                // 剩余领券次数更新
                if (isCheckLeftRecvNum && recvRes.leftRecvNum !== undefined) {
                    storeCommon.leftRecvNum = recvRes.leftRecvNum;
                    eventBus.$emit('selfCoupon-changeTimes', storeCommon.leftRecvNum);
                }
                // 领商家券三次弹出一次
                if (callFetchOneCouponCount % 3 === 0) {
                    experimentTrafficMiniProgramModal();
                } else {
                    Toast({ message: '领取成功', position: 'bottom' });
                }
                callFetchOneCouponCount += 1;
            } else if (recvRes && recvRes.rets && recvRes.rets[0] === 40) {
                // rets === 40 表示商家券无库存
                // 触发领券成功的回调
                const eventName = getGiftEventName(giftId);
                eventBus.$emit(eventName, COUPON_STATUS.STATUS_NEW, COUPON_REMAIN_STATUS.REMAIN_STATUS_NONE);
                Toast({ message: '券已经被抢光啦', position: 'bottom' });
            } else {
                Toast({ message: '领取失败', position: 'bottom' });
            }
        };
        if (giftInfo.status === COUPON_STATUS.STATUS_NEW) {
            if (isCheckLeftRecvNum) {
                if (storeCommon.leftRecvNum > 0) {
                    await getCoupon();
                } else {
                    Toast({ message: '今日领券机会已用完' });
                }
            } else {
                await getCoupon();
            }
        }
    } catch (error) {
        addCouponErrorToReport(`单张券领取异常，${getErrorMessage(error)}`, '单张券领取异常');
        handleError(error, { showMessage: true, showConsole: true });
    }
};
